import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BackupIcon from '@mui/icons-material/Backup';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { useTranslation } from '../app/LanguageContext';
import { getMyProfile } from '../app/api';
import { getToken } from '../app/auth';
import UserMenu from '../components/UserMenu';
import ThemeModeToggle from '../components/ThemeModeToggle';
import WorkspaceSelector from '../components/WorkspaceSelector';
import { useWorkspace } from '../app/WorkspaceContext';
import { useExtensions } from '../app/ExtensionsContext';
import { EXTENSION_CATALOG } from '../extensions/extensionRegistry';
import { getDrawerOpenForPath, setDrawerOpenForPath } from './drawerPreferenceStorage';
import { PageTitleProvider, usePageTitle } from './PageTitleContext';
import { resolveLayoutPageTitle } from './resolveLayoutPageTitle';

const drawerWidth = 240;

function ExtensionSidebarGroup({ ext, translate, handleDrawerLinkClick, location }) {
  const basePath = ext.menuItem.basePath;
  const [open, setOpen] = useState(() => location.pathname.startsWith(basePath));
  useEffect(() => {
    if (location.pathname.startsWith(basePath)) setOpen(true);
  }, [location.pathname, basePath]);
  const Icon = ext.icon;
  return (
    <>
      <ListItem disablePadding>
        <ListItemButton onClick={() => setOpen((o) => !o)} selected={location.pathname.startsWith(basePath)}>
          <ListItemIcon>
            <Icon />
          </ListItemIcon>
          <ListItemText primary={translate(ext.menuItem.labelKey)} />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {ext.menuItem.children.map((child) => (
            <ListItem key={child.path} disablePadding sx={{ pl: 4 }}>
              <ListItemButton
                component={RouterLink}
                to={child.path}
                selected={
                  child.end
                    ? location.pathname === child.path
                    : location.pathname === child.path || location.pathname.startsWith(`${child.path}/`)
                }
                onClick={handleDrawerLinkClick}
              >
                <ListItemText primary={translate(child.labelKey)} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
}

const AppLayoutBody = ({ children }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const { translate } = useTranslation();
  const { pageTitleOverride } = usePageTitle();
  const { workspaces, loading } = useWorkspace();
  const { isEnabled, loading: extensionsLoading, loadError: extensionsLoadError } = useExtensions();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopPeekOpen, setDesktopPeekOpen] = useState(false);
  const peekCloseTimerRef = useRef(null);
  const [user, setUser] = useState(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(() =>
    location.pathname.startsWith('/settings')
  );

  useEffect(() => {
    if (!isDesktop) {
      setMobileOpen(false);
      return;
    }
    const saved = getDrawerOpenForPath(location.pathname);
    if (saved !== undefined) {
      setMobileOpen(saved);
    } else {
      setMobileOpen(true);
    }
  }, [location.pathname, isDesktop]);

  const clearPeekCloseTimer = useCallback(() => {
    if (peekCloseTimerRef.current != null) {
      clearTimeout(peekCloseTimerRef.current);
      peekCloseTimerRef.current = null;
    }
  }, []);

  const schedulePeekClose = useCallback(() => {
    clearPeekCloseTimer();
    peekCloseTimerRef.current = setTimeout(() => {
      peekCloseTimerRef.current = null;
      setDesktopPeekOpen(false);
    }, 220);
  }, [clearPeekCloseTimer]);

  useEffect(() => {
    return () => clearPeekCloseTimer();
  }, [clearPeekCloseTimer]);

  useEffect(() => {
    if (!isDesktop) {
      clearPeekCloseTimer();
      setDesktopPeekOpen(false);
    }
  }, [isDesktop, clearPeekCloseTimer]);

  useEffect(() => {
    if (mobileOpen) {
      clearPeekCloseTimer();
      setDesktopPeekOpen(false);
    }
  }, [mobileOpen, clearPeekCloseTimer]);

  useEffect(() => {
    clearPeekCloseTimer();
    setDesktopPeekOpen(false);
  }, [location.pathname, clearPeekCloseTimer]);

  useEffect(() => {
    if (user && !loading && workspaces.length === 0 && location.pathname !== '/workspaces/new') {
      navigate('/workspaces/new', { replace: true });
    }
  }, [user, loading, workspaces.length, location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname.startsWith('/settings')) {
      setSettingsMenuOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const loadUser = async () => {
      const token = getToken();
      if (!token) {
        return;
      }
      try {
        const response = await getMyProfile();
        if (response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
    };
    loadUser();
  }, []);

  const handleDrawerToggle = () => {
    const next = !mobileOpen;
    setMobileOpen(next);
    setDrawerOpenForPath(location.pathname, next);
  };

  const handlePinSidebar = () => {
    clearPeekCloseTimer();
    setDesktopPeekOpen(false);
    setMobileOpen(true);
    setDrawerOpenForPath(location.pathname, true);
  };

  const closeDesktopPeek = useCallback(() => {
    clearPeekCloseTimer();
    setDesktopPeekOpen(false);
  }, [clearPeekCloseTimer]);

  const handleDrawerLinkClick = useCallback(
    (event) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        (typeof event.button === 'number' && event.button !== 0)
      ) {
        return;
      }
      if (!isDesktop) {
        setMobileOpen(false);
      } else {
        clearPeekCloseTimer();
        setDesktopPeekOpen(false);
      }
    },
    [isDesktop, clearPeekCloseTimer]
  );

  const showMenu = workspaces.length > 0 || loading;

  const pageTitle =
    pageTitleOverride ?? resolveLayoutPageTitle(translate, location.pathname);

  const primaryMenuItems = [
    { text: translate('common.home'), icon: <HomeIcon />, path: '/' },
    { text: translate('common.users'), icon: <PeopleIcon />, path: '/users' },
    { text: translate('common.customers'), icon: <PersonIcon />, path: '/customers' },
    { text: translate('common.products'), icon: <ShoppingCartIcon />, path: '/products' },
    { text: translate('common.orders'), icon: <ReceiptIcon />, path: '/orders' },
    { text: translate('common.medias'), icon: <PhotoLibraryIcon />, path: '/medias' },
  ];

  const isNavSelected = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const renderDrawerList = (options = {}) => {
    const { showPinSidebar } = options;
    return (
    <List>
      {primaryMenuItems.map((item) => (
        <ListItem key={item.path} disablePadding>
          <ListItemButton
            component={RouterLink}
            to={item.path}
            selected={isNavSelected(item.path)}
            onClick={handleDrawerLinkClick}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        </ListItem>
      ))}
      {extensionsLoading &&
        !extensionsLoadError &&
        EXTENSION_CATALOG.filter((ext) => ext.menuItem && !ext.menuItem.children).map((ext) => (
          <ListItem key={`ext-menu-skeleton-${ext.id}`} disablePadding>
            <Box
              component="div"
              sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, width: '100%', gap: 1 }}
              aria-hidden
            >
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="text" width="55%" sx={{ flexGrow: 0 }} />
            </Box>
          </ListItem>
        ))}
      {extensionsLoading &&
        !extensionsLoadError &&
        EXTENSION_CATALOG.filter((ext) => ext.menuItem?.children?.length).map((ext) => (
          <ListItem key={`ext-menu-skel-grp-${ext.id}`} disablePadding>
            <Box
              component="div"
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 2, py: 1, width: '100%' }}
              aria-hidden
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width="55%" sx={{ flexGrow: 0 }} />
              </Box>
              <Skeleton variant="text" width="70%" sx={{ ml: 4 }} />
              <Skeleton variant="text" width="65%" sx={{ ml: 4 }} />
            </Box>
          </ListItem>
        ))}
      {EXTENSION_CATALOG.filter((ext) => !extensionsLoading && isEnabled(ext.id) && ext.menuItem && !ext.menuItem.children).map((ext) => {
        const Icon = ext.icon;
        return (
          <ListItem key={ext.menuItem.path} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={ext.menuItem.path}
              selected={isNavSelected(ext.menuItem.path)}
              onClick={handleDrawerLinkClick}
            >
              <ListItemIcon><Icon /></ListItemIcon>
              <ListItemText primary={translate(ext.menuItem.labelKey)} />
            </ListItemButton>
          </ListItem>
        );
      })}
      {EXTENSION_CATALOG.filter((ext) => !extensionsLoading && isEnabled(ext.id) && ext.menuItem?.children?.length).map(
        (ext) => (
          <ExtensionSidebarGroup
            key={ext.id}
            ext={ext}
            translate={translate}
            handleDrawerLinkClick={handleDrawerLinkClick}
            location={location}
          />
        )
      )}
      {user?.profile === 'ADMIN' && (
        <ListItem key="/admin/backup" disablePadding>
          <ListItemButton
            component={RouterLink}
            to="/admin/backup"
            selected={isNavSelected('/admin/backup')}
            onClick={handleDrawerLinkClick}
          >
            <ListItemIcon>
              <BackupIcon />
            </ListItemIcon>
            <ListItemText primary={translate('admin_backup.menu')} />
          </ListItemButton>
        </ListItem>
      )}
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => setSettingsMenuOpen((o) => !o)}
          selected={location.pathname.startsWith('/settings')}
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary={translate('common.settings')} />
          {settingsMenuOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={settingsMenuOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItem disablePadding sx={{ pl: 4 }}>
            <ListItemButton
              component={RouterLink}
              to="/settings"
              selected={location.pathname === '/settings'}
              onClick={handleDrawerLinkClick}
            >
              <ListItemText primary={translate('settings.menu_general')} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ pl: 4 }}>
            <ListItemButton
              component={RouterLink}
              to="/settings/order-workflow/catalog"
              selected={location.pathname.startsWith('/settings/order-workflow')}
              onClick={handleDrawerLinkClick}
            >
              <ListItemText primary={translate('settings.menu_order_workflow')} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ pl: 4 }}>
            <ListItemButton
              component={RouterLink}
              to="/extensions"
              selected={location.pathname === '/extensions'}
              onClick={handleDrawerLinkClick}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ExtensionOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={translate('extensions.menu')} />
            </ListItemButton>
          </ListItem>
        </List>
      </Collapse>
      {showPinSidebar && (
        <ListItem disablePadding sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}>
          <ListItemButton onClick={handlePinSidebar}>
            <ListItemIcon>
              <PushPinOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={translate('common.pin_sidebar')} />
          </ListItemButton>
        </ListItem>
      )}
    </List>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          disableGutters={false}
          sx={{
            position: 'relative',
            gap: { xs: 0.5, sm: 1 },
            px: { xs: 1, sm: 2 },
            minHeight: { xs: 56, sm: 64 },
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, zIndex: 1 }}>
            {showMenu && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                onMouseEnter={() => {
                  if (isDesktop && !mobileOpen) {
                    clearPeekCloseTimer();
                    setDesktopPeekOpen(true);
                  }
                }}
                onMouseLeave={() => {
                  if (isDesktop && !mobileOpen) {
                    schedulePeekClose();
                  }
                }}
                sx={{ mr: { xs: 0.5, sm: 1 } }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
          <Typography
            variant="h6"
            noWrap
            component="h1"
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: {
                xs: 'calc(100% - 120px)',
                sm: 'calc(100% - 260px)',
                md: 'calc(100% - 320px)',
              },
              minWidth: 0,
              fontSize: { xs: '0.95rem', sm: '1.25rem' },
              fontWeight: 600,
              letterSpacing: '-0.01em',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            {pageTitle}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              flexShrink: 0,
              ml: 'auto',
              zIndex: 1,
            }}
          >
            <ThemeModeToggle />
            {user && (
              <>
                {showMenu && <WorkspaceSelector />}
                <UserMenu user={user} />
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      {showMenu && (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        <Box>{renderDrawerList()}</Box>
      </Drawer>
      )}
      {showMenu && (
        <Drawer
          variant="temporary"
          open={desktopPeekOpen}
          onClose={closeDesktopPeek}
          ModalProps={{
            keepMounted: true,
            disableScrollLock: true,
            BackdropProps: { invisible: true },
          }}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              top: 64,
              height: 'calc(100% - 64px)',
            },
          }}
        >
          <Box
            onMouseEnter={() => {
              if (isDesktop) {
                clearPeekCloseTimer();
                setDesktopPeekOpen(true);
              }
            }}
            onMouseLeave={() => {
              if (isDesktop && !mobileOpen) {
                schedulePeekClose();
              }
            }}
          >
            {renderDrawerList({ showPinSidebar: true })}
          </Box>
        </Drawer>
      )}
      {showMenu && mobileOpen && (
        <Drawer
          variant="persistent"
          open={mobileOpen}
          sx={{
            display: { xs: 'none', sm: 'block' },
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              top: 64,
            },
          }}
        >
          <Box>{renderDrawerList()}</Box>
        </Drawer>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3 },
          pt: { xs: 1.5, sm: 3 },
          bgcolor: 'background.default',
          width: { sm: showMenu && mobileOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          maxWidth: '100vw',
          ml: { sm: showMenu && mobileOpen ? `${drawerWidth}px` : 0 },
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

const AppLayout = ({ children }) => (
  <PageTitleProvider>
    <AppLayoutBody>{children}</AppLayoutBody>
  </PageTitleProvider>
);

export default AppLayout;


